import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from 'src/api/apiClient';
import {
  RULE_DEFINITION_V1,
  RULE_DEFINITION_TEMPLATES_V1,
  // CREATE_LIST_PLAYBOOK,
  PLAYBOOKS_BASE_URL,
} from 'src/constants/httpEndPoints';
import { addFilterTypeToSelectedFilters, createFilterTypeMap } from 'src/features/Dashboard/utils';
import { parseFiltersForPlaybook } from 'src/features/settings/modules/icpFiltersConfiguration/playbookHelpers/parseFiltersForPlaybook';
import { getCreateListPayload, getFiltersFromNode } from 'src/features/workflows/utils';
import {
  applyDynamicTemplateOptionsToNodes,
  applyCustomAiSignalToNodes,
} from 'src/features/workflows/utils/dynamicTemplateOptions';

export type OwnerOption = { label: string; value: string };

export type PlaybookAggregate = {
  criteria?: Record<string, unknown>;
  sourceContactsFilters?: Record<string, unknown>;
  enrichment?: { enrichWithEmail: boolean; enrichWithPhone: boolean };
  assignContactsTo?: OwnerOption[];
  addToCampaign?: {
    selectedCMP: string;
    selectedSequence: string;
    selectedMailBox?: string;
  };
};

export type PlaybookDetails = {
  id?: number | string;
  name?: string; // playbook name in backend is `name` for read; create uses `playbookName`
  playbookName?: string; // used for create/update payloads
  description?: string;
  desc?: string;
  entityType?: 'CONTACT' | 'ACCOUNT' | string;
  aggregate?: PlaybookAggregate;
  nodes?: Array<Record<string, unknown>>;
  edges?: Array<Record<string, unknown>>;
  templateType?: string;
  triggerType?: string;
  icon?: string;
  tags?: string[];
  metaData?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  isEdit?: boolean;
  selectedNodeId?: string | null;
  templateId?: number | string | null;
  status?: 'DRAFT' | 'ACTIVE' | string;
};

export type PlaybookSummary = {
  id: number | string;
  name: string;
  description?: string;
  entityType?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type TemplateDetails = {
  id?: number | string;
  name?: string;
  description?: string;
  entityType?: 'CONTACT' | 'ACCOUNT' | string;
  nodes?: Array<Record<string, unknown>>;
  edges?: Array<Record<string, unknown>>;
  templateType?: string;
  triggerType?: string;
  icon?: string;
  tags?: string[];
  metaData?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  status?: 'DRAFT' | 'ACTIVE' | string;
};

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

type PlaybooksState = {
  current: {
    data: PlaybookDetails | null;
    status: AsyncStatus;
    error: string | null;
  };
  list: {
    items: PlaybookSummary[];
    status: AsyncStatus;
    error: string | null;
  };
  template: {
    data: TemplateDetails | null;
    status: AsyncStatus;
    error: string | null;
  };
  filtersHashForTypes: any;
};

const initialState: PlaybooksState = {
  current: { data: null, status: 'idle', error: null },
  list: { items: [], status: 'idle', error: null },
  template: { data: null, status: 'idle', error: null },
  filtersHashForTypes: null,
};

type CommonHeaders = {
  emailId: string;
  uniqueId: string;
};

// Thunks
export const fetchPlaybooks = createAsyncThunk<PlaybookSummary[], { headers: CommonHeaders }>(
  'playbooks/fetchAll',
  async ({ headers }, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`${RULE_DEFINITION_V1}/`, { headers });
      const list = (res.data?.playbooks || res.data || []) as any[];
      return list.map((item: any) => ({
        id: item.id,
        name: item.name,
        description: item.desc ?? item.description,
        entityType: item.entityType,
        createdAt: item.created_at ?? item.createdAt,
        updatedAt: item.updated_at ?? item.updatedAt,
      }));
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || 'Failed to fetch playbooks');
    }
  },
);

export const fetchPlaybookById = createAsyncThunk<
  PlaybookDetails,
  { id: string | number; headers: CommonHeaders }
>('playbooks/fetchById', async ({ id, headers }, { rejectWithValue }) => {
  try {
    const res = await apiClient.get(`${RULE_DEFINITION_V1}/${id}`, { headers });
    const data = res.data || {};
    const details: PlaybookDetails = {
      id: data.id ?? id,
      name: data.name,
      playbookName: data.name,
      description: data.description,
      desc: data.desc ?? data.description,
      entityType: data.entityType,
      nodes: (data.nodes as Array<Record<string, unknown>>) || [],
      edges: (data.edges as Array<Record<string, unknown>>) || [],
      templateType: data.templateType,
      triggerType: data.triggerType,
      icon: data.icon,
      tags: (data.tags as string[]) || [],
      metaData: data.metaData,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      templateId: data.templateId ?? null,
      status: data.status,
      aggregate: {
        criteria: (data.filters as Record<string, unknown>) || {},
      },
    };
    return details;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.message || 'Failed to fetch playbook');
  }
});

// Helper: build backend payload for create/update
const buildPlaybookPayload = (input: {
  payload: {
    playbookName: string;
    status?: 'DRAFT' | 'ACTIVE' | string;
    entityType?: string;
    desc?: string;
    icon?: string;
    tags?: string[];
    nodes?: Array<Record<string, unknown>>;
    edges?: Array<Record<string, unknown>>;
    templateId?: number | string | null;
    selectedCMP?: string;
    selectedSequence?: string; // JSON stringified or id string
    selectedMailBox?: string; // JSON stringified mailbox
    triggerType?: string;
    templateType?: string;
    metaData?: Record<string, unknown>;
    filters?: Array<Record<string, unknown>>;
    trigger?: Record<string, unknown>;
  };
  headers: CommonHeaders;
}) => {
  const { payload, headers } = input;
  // Parse sequence/mailbox if stringified
  let externalSequenceId: string | undefined;
  let sourceCmpName: string | undefined;
  if (payload.selectedSequence) {
    try {
      const parsed = JSON.parse(payload.selectedSequence as any);
      externalSequenceId = parsed?.externalSequenceId;
      sourceCmpName = parsed?.sourceCmpName;
    } catch (_) {
      // assume direct id - no longer needed for sequenceId
    }
  }

  return {
    playbookName: payload.playbookName,
    status: (payload.status as any) || 'DRAFT', // Default to DRAFT if not specified
    filters: (payload as any).filters || [],
    templateId: payload.templateId ?? null,
    tenant_id: headers.uniqueId,
    externalSequenceId,
    sourceCmpName,
    triggerType: payload.triggerType,
    templateType: payload.templateType,
    metaData: payload.metaData,
    entityType: payload.entityType || 'CONTACT',
    // Backend derives nodes/edges from template on create; allow update to send nodes/edges
    nodes: payload.nodes || [],
    edges: payload.edges || [],
    trigger: payload.trigger,
  } as any;
};

export const createPlaybook = createAsyncThunk<
  PlaybookDetails,
  { payload: any; headers: CommonHeaders },
  { dispatch: any }
>('playbooks/create', async ({ payload, headers }, { rejectWithValue, getState, dispatch }) => {
  try {
    // Get template data from state if available
    const state = getState() as any;
    const template = state.playbooks?.template?.data;

    // If template exists and we're creating from template, merge template data

    // Process filters from nodes
    let finalFilters: any[] = [];
    const filter = getFiltersFromNode(payload.nodes);
    if (filter && typeof filter === 'object') {
      const FilterData = parseFiltersForPlaybook(filter as any);
      const dictionary = createFilterTypeMap(state.playbooks.filtersHashForTypes);
      finalFilters = addFilterTypeToSelectedFilters(
        FilterData.filter((item: any) => {
          if (
            item?.includes?.length > 0 ||
            item?.excludes?.length > 0 ||
            item?.valueRange ||
            item?.min ||
            item?.max ||
            item?.dateRange ||
            item?.toDate ||
            item?.fromDate ||
            item?.range ||
            item?.ranges
          ) {
            return true;
          }
          return false;
        }),
        dictionary,
      );
    }

    // let playbookResponse: any;

    // If template exists and we're creating from template, merge template data
    // if (template) {
    // For workflow-editor playbooks, use specific templateType as triggerType when available
    // This enables better matching with the backend filtering improvements
    const triggerType = payload?.templateType || template?.templateType;
    const trigger = {
      entityType: payload?.entityType || template?.entityType || 'CONTACT',
      triggerType: triggerType || 'WORKFLOW', // Fallback to WORKFLOW for generic triggers
      source: 'ANY',
      // triggerValue: template.entityType === 'CONTACT' ? 'CONTACT_CREATED' : 'COMPANY_CREATED',
    };
    const enhancedPayload = {
      ...payload,
      entityType: payload?.entityType || template?.entityType || 'CONTACT',
      triggerType: payload?.triggerType || template?.templateType,
      templateType: payload?.templateType || template?.templateType,
      icon: payload?.icon || template?.icon,
      tags: payload?.tags || template?.tags || [],
      metaData: payload?.metaData || template?.metaData || {},
      // Add fields that buildPlaybookPayload expects
      filters: finalFilters || [],
      selectedCMP: payload.selectedCMP,
      selectedSequence: payload.selectedSequence,
      selectedMailBox: payload.selectedMailBox,
      trigger,
    };

    const backendPayload = buildPlaybookPayload({ payload: enhancedPayload, headers });
    console.log('Enhanced payload for template playbook:', enhancedPayload);
    console.log('Backend payload for template playbook:', backendPayload);
    const res = await apiClient.post(RULE_DEFINITION_V1, backendPayload, { headers });
    const playbookResponse = res.data;
    // }
    // } else {
    //   // Use original payload if no template or not creating from template
    //   const backendPayload = buildPlaybookPayload({ payload, headers });
    //   const res = await apiClient.post(RULE_DEFINITION_V1, backendPayload, { headers });
    //   playbookResponse = res.data;
    // }

    // Create list after successful playbook creation
    // const createListPayload = getCreateListPayload({
    //   ...payload,
    //   filters: finalFilters,
    //   tenant_id: headers.uniqueId,
    // });

    // if (playbookResponse?.id && createListPayload && Object.keys(createListPayload).length > 0) {
    //   try {
    //     await dispatch(
    //       createListForPlaybook({
    //         createListPayload: {
    //           ...createListPayload,
    //           tenant_id: headers.uniqueId,
    //         },
    //         headers,
    //       }),
    //     ).unwrap();
    //     console.log('List created successfully for playbook:', playbookResponse.id);
    //   } catch (listError) {
    //     console.warn('Failed to create list for playbook:', listError);
    //     // Don't fail the entire operation if list creation fails
    //   }
    // }

    return playbookResponse as PlaybookDetails;
  } catch (err: any) {
    console.error('Create playbook error:', err);
    console.error('Error response:', err?.response?.data);
    console.error('Error status:', err?.response?.status);
    return rejectWithValue(
      err?.response?.data?.message || err?.message || 'Failed to create playbook',
    );
  }
});

export const updatePlaybook = createAsyncThunk<
  PlaybookDetails,
  { id: string | number; payload: any; headers: CommonHeaders },
  { dispatch: any }
>('playbooks/update', async ({ id, payload, headers }, { rejectWithValue,getState, dispatch }) => {
  try {
    // Process filters from nodes
    const state = getState() as any;
    let finalFilters: any[] = [];
    const filter = getFiltersFromNode(payload.nodes);
     if (filter && typeof filter === 'object') {
      const FilterData = parseFiltersForPlaybook(filter as any);
      const dictionary = createFilterTypeMap(state.playbooks.filtersHashForTypes);
      finalFilters = addFilterTypeToSelectedFilters(
        FilterData.filter((item: any) => {
          if (
            item?.includes?.length > 0 ||
            item?.excludes?.length > 0 ||
            item?.valueRange ||
            item?.min ||
            item?.max ||
            item?.dateRange ||
            item?.toDate ||
            item?.fromDate ||
            item?.range ||
            item?.ranges
          ) {
            return true;
          }
          return false;
        }),
        dictionary,
      );
    }

    // For workflow-editor playbooks, use specific templateType as triggerType when available
    // This enables better matching with the backend filtering improvements  
    const triggerType = payload?.templateType;
    const trigger = {
      entityType: payload?.entityType || 'CONTACT',
      triggerType: triggerType || 'WORKFLOW', // Fallback to WORKFLOW for generic triggers
      source: 'ANY',
      // triggerValue: template.entityType === 'CONTACT' ? 'CONTACT_CREATED' : 'COMPANY_CREATED',
    };

    const enhancedPayload = {
      ...payload,
      entityType: payload?.entityType || 'CONTACT',
      triggerType: payload?.triggerType,
      templateType: payload?.templateType,
      icon: payload?.icon,
      tags: payload?.tags || [],
      metaData: payload?.metaData || {},
      // Add fields that buildPlaybookPayload expects
      filters: finalFilters || [],
      trigger,
    };
    const backendPayload = buildPlaybookPayload({ payload: enhancedPayload, headers });
    const res = await apiClient.put(`${RULE_DEFINITION_V1}/${id}`, backendPayload, {
      headers,
    });
    const playbookResponse = res.data as PlaybookDetails;

    // Create list after successful playbook update
    // const createListPayload = getCreateListPayload({
    //   ...payload,
    //   filters: payload.filters,
    //   tenant_id: headers.uniqueId,
    // });

    // if (playbookResponse?.id && createListPayload && Object.keys(createListPayload).length > 0) {
    //   try {
    //     await dispatch(
    //       createListForPlaybook({
    //         createListPayload: {
    //           ...createListPayload,
    //           tenant_id: headers.uniqueId,
    //         },
    //         headers,
    //       }),
    //     ).unwrap();
    //     console.log('List updated successfully for playbook:', playbookResponse.id);
    //   } catch (listError) {
    //     console.warn('Failed to update list for playbook:', listError);
    //     // Don't fail the entire operation if list creation fails
    //   }
    // }

    return playbookResponse;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.message || 'Failed to update playbook');
  }
});

export const deletePlaybook = createAsyncThunk<
  { id: string | number },
  { id: string | number; headers: CommonHeaders }
>('playbooks/delete', async ({ id, headers }, { rejectWithValue }) => {
  try {
    await apiClient._delete(`${RULE_DEFINITION_V1}/${id}`, { headers });
    return { id };
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.message || 'Failed to delete playbook');
  }
});

// Fetch template to start a playbook from it
export const fetchTemplateForPlaybook = createAsyncThunk<
  {
    nodes: Array<Record<string, unknown>>;
    edges: Array<Record<string, unknown>>;
    templateId: number | string;
    template: TemplateDetails;
  },
  {
    templateId: string | number;
    headers: CommonHeaders;
    customAiSignalName?: string;
    customAiSignalColumn?: string;
  }
>(
  'playbooks/fetchTemplateForPlaybook',
  async (
    { templateId, headers, customAiSignalName, customAiSignalColumn },
    { rejectWithValue },
  ) => {
    try {
      const res = await apiClient.get(`${RULE_DEFINITION_TEMPLATES_V1}/${templateId}`, { headers });
      const data = res.data || {};
      const template: TemplateDetails = {
        id: data.id ?? templateId,
        name: data.name,
        description: data.description,
        entityType: data.entityType,
        nodes: (data.nodes as Array<Record<string, unknown>>) || [],
        edges: (data.edges as Array<Record<string, unknown>>) || [],
        templateType: data.templateType,
        triggerType: data.triggerType,
        icon: data.icon,
        tags: (data.tags as string[]) || [],
        metaData: data.metaData,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        status: data?.status,
      };
      // Apply dynamic options and custom AI signal (if provided)
      const rawNodes = ((data.nodes as Array<Record<string, unknown>>) || []) as any[];
      let enhancedNodes = await applyDynamicTemplateOptionsToNodes(
        { name: data.name },
        rawNodes,
        headers,
      );

      // If user selected a specific Custom AI Signal template instance, inject it
      // and enrich template metadata with columnName passed from selection modal
      let enrichedTemplateMeta: Record<string, unknown> | undefined = undefined;
      if (data?.name === 'Custom Ai Signal' && customAiSignalName) {
        enhancedNodes = applyCustomAiSignalToNodes(enhancedNodes, customAiSignalName) as any[];
        if (customAiSignalColumn) {
          enrichedTemplateMeta = {
            ...(data.metaData || {}),
            columnName: customAiSignalColumn,
            mappedColumns: {
              [customAiSignalName]: customAiSignalColumn + 'Sentiment',
            },
          } as Record<string, unknown>;
        }
        if (enrichedTemplateMeta) {
          template.metaData = enrichedTemplateMeta as any;
        }
      }
      return {
        nodes: enhancedNodes as Array<Record<string, unknown>>,
        edges: (data.edges as Array<Record<string, unknown>>) || [],
        templateId: (data.id as number | string) ?? templateId,
        template,
      };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || 'Failed to fetch template for playbook',
      );
    }
  },
);

// Create list after playbook creation
export const createListForPlaybook = createAsyncThunk<
  any,
  { createListPayload: any; headers: CommonHeaders }
>('playbooks/createList', async ({ createListPayload, headers }, { rejectWithValue }) => {
  try {
    const payload = {
      ...createListPayload,
    };
    const res = await apiClient.post(PLAYBOOKS_BASE_URL, payload, { headers });
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.message || 'Failed to create list for playbook');
  }
});

// Create list after playbook creation based on node
export const createListForPlaybookBasedOnNode = createAsyncThunk<
  any,
  { createListPayload: any; headers: CommonHeaders }
>('playbooks/nodebased-createList', async ({ createListPayload, headers }, { rejectWithValue }) => {
  try {
    const payload = {
      ...createListPayload,
    };
    const res = await apiClient.post(`${PLAYBOOKS_BASE_URL}/create-list`, payload, { headers });
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.message || 'Failed to create list for playbook');
  }
});

export const sendUpdatedPlaybookStatus = createAsyncThunk<
  any,
  { updateStatusPayload: any; headers: CommonHeaders }
>('playbooks/updated-status', async ({ updateStatusPayload, headers }, { rejectWithValue }) => {
  try {
    const payload = {
      ...updateStatusPayload,
    };
    const res = await apiClient.post(`${PLAYBOOKS_BASE_URL}/update-status`, payload, { headers });
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.message || 'Failed to update playbook status');
  }
});

// Import types for JSON workflow creation
import type {
  WorkflowJSON,
  SimplifiedWorkflowJSON,
  WorkflowJSONValidationResult,
  WorkflowJSONOptions,
} from 'src/features/workflows/json/types';
import {
  jsonWorkflowService,
  validateWorkflowJSON,
} from 'src/features/workflows/json/services/jsonWorkflowService';

/**
 * Result type for createPlaybookFromJSON thunk
 */
export type CreatePlaybookFromJSONResult = {
  playbook: PlaybookDetails;
  validation: WorkflowJSONValidationResult;
};

/**
 * Create a playbook from JSON (full or simplified format)
 *
 * @param json - Workflow JSON in either full or simplified format
 * @param options - Creation options (autoLayout, autoGenerateIds, etc.)
 * @param headers - Common headers for API requests
 */
export const createPlaybookFromJSON = createAsyncThunk<
  CreatePlaybookFromJSONResult,
  {
    json: string | WorkflowJSON | SimplifiedWorkflowJSON;
    options?: WorkflowJSONOptions;
    headers: CommonHeaders;
  }
>(
  'playbooks/createFromJSON',
  async ({ json, options = {}, headers }, { rejectWithValue, dispatch }) => {
    try {
      // Use the JSON workflow service to process and validate
      const result = await jsonWorkflowService.createFromJSON(json, {
        autoLayout: options.autoLayout ?? true,
        autoGenerateIds: options.autoGenerateIds ?? true,
        validateOnly: options.validateOnly ?? false,
      });

      // If validation failed, reject with the validation errors
      if (!result.success || !result.data) {
        const errorMessages = result.validation.errors
          .map((e) => `${e.code}: ${e.message}`)
          .join('; ');
        return rejectWithValue({
          message: errorMessages || 'Workflow JSON validation failed',
          validation: result.validation,
        });
      }

      // If validate only, return early without creating
      if (options.validateOnly) {
        return {
          playbook: {} as PlaybookDetails,
          validation: result.validation,
        };
      }

      // Prepare payload for backend
      const workflowJSON = result.data.workflow;
      const payload = {
        playbookName: workflowJSON.playbookName,
        status: workflowJSON.status || 'DRAFT',
        entityType: workflowJSON.entityType || 'CONTACT',
        nodes: workflowJSON.nodes,
        edges: workflowJSON.edges,
        templateType: workflowJSON.templateType,
        triggerType: workflowJSON.triggerType,
        metaData: workflowJSON.metadata,
        desc: workflowJSON.description,
      };

      // Create the playbook via API
      const res = await apiClient.post(RULE_DEFINITION_V1, payload, { headers });
      const playbookResponse = res.data as PlaybookDetails;

      return {
        playbook: playbookResponse,
        validation: result.validation,
      };
    } catch (err: any) {
      console.error('Create playbook from JSON error:', err);
      return rejectWithValue({
        message: err?.response?.data?.message || err?.message || 'Failed to create playbook from JSON',
        validation: err?.validation,
      });
    }
  }
);

/**
 * Validate workflow JSON without creating
 */
export const validatePlaybookJSON = createAsyncThunk<
  WorkflowJSONValidationResult,
  { json: string | WorkflowJSON | SimplifiedWorkflowJSON }
>('playbooks/validateJSON', async ({ json }, { rejectWithValue }) => {
  try {
    const validation = validateWorkflowJSON(json);
    return validation;
  } catch (err: any) {
    return rejectWithValue({
      message: err?.message || 'Failed to validate workflow JSON',
    });
  }
});

const playbooksSlice = createSlice({
  name: 'playbooks',
  initialState,
  reducers: {
    clearCurrentPlaybook(state) {
      state.current = { data: null, status: 'idle', error: null };
    },
    setCurrentPlaybook(state, action: PayloadAction<PlaybookDetails>) {
      state.current.data = action.payload;
      state.current.status = 'succeeded';
      state.current.error = null;
    },
    updateCurrentPlaybook(state, action: PayloadAction<Partial<PlaybookDetails>>) {
      state.current.data = { ...(state.current.data || {}), ...action.payload };
    },
    updatePlaybookField(
      state,
      action: PayloadAction<{ key: keyof PlaybookDetails | string; value: unknown }>,
    ) {
      if (!state.current.data) state.current.data = {} as PlaybookDetails;
      (state.current.data as any)[action.payload.key] = action.payload.value as any;
    },
    setIsEdit(state, action: PayloadAction<boolean>) {
      if (!state.current.data) state.current.data = {} as PlaybookDetails;
      state.current.data.isEdit = action.payload;
    },
    setSelectedNodeId(state, action: PayloadAction<string | null>) {
      if (!state.current.data) state.current.data = {} as PlaybookDetails;
      state.current.data.selectedNodeId = action.payload ?? null;
    },
    setNodesEdges(
      state,
      action: PayloadAction<{
        nodes?: Array<Record<string, unknown>>;
        edges?: Array<Record<string, unknown>>;
      }>,
    ) {
      if (!state.current.data) state.current.data = {} as PlaybookDetails;
      if (action.payload.nodes) state.current.data.nodes = action.payload.nodes;
      if (action.payload.edges) state.current.data.edges = action.payload.edges;
      if (
        action.payload.nodes &&
        action.payload.nodes.length > 0 &&
        state.current.data.selectedNodeId == null
      ) {
        const nodesArr = action.payload.nodes as Array<any>;
        state.current.data.selectedNodeId = nodesArr[0]?.id || null;
      }
    },
    updateNodeData(
      state,
      action: PayloadAction<{ nodeId: string; patch: Record<string, unknown> }>,
    ) {
      const nodes = (state.current.data?.nodes || []) as Array<any>;
      const idx = nodes.findIndex((n) => n.id === action.payload.nodeId);
      if (idx >= 0) {
        const prev = nodes[idx];
        const nextData = { ...(prev.data || {}), ...action.payload.patch };
        const keys = Object.keys(action.payload.patch);
        const noChange = keys.every((k) => (prev.data || {})[k] === nextData[k]);
        if (noChange) return;
        nodes[idx] = { ...prev, data: nextData };
        state.current.data = {
          ...(state.current.data || {}),
          nodes: [...nodes],
        } as PlaybookDetails;
      }
    },
    upsertPlaybookInList(state, action: PayloadAction<PlaybookSummary>) {
      const idx = state.list.items.findIndex((t) => t.id === action.payload.id);
      if (idx >= 0) state.list.items[idx] = action.payload;
      else state.list.items.unshift(action.payload);
    },
    clearTemplate(state) {
      state.template = { data: null, status: 'idle', error: null };
    },
    setTemplate(state, action: PayloadAction<TemplateDetails>) {
      if (!state.template) {
        state.template = { data: null, status: 'idle', error: null };
      }
      state.template.data = action.payload;
      state.template.status = 'succeeded';
      state.template.error = null;
    },
    setFiltersHashForTypes(state, action: PayloadAction<any>) {
      state.filtersHashForTypes = action.payload;
    },
    clearFiltersHashForTypes(state) {
      state.filtersHashForTypes = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlaybooks.pending, (state) => {
        state.list.status = 'loading';
        state.list.error = null;
      })
      .addCase(fetchPlaybooks.fulfilled, (state, action) => {
        state.list.status = 'succeeded';
        state.list.items = action.payload;
      })
      .addCase(fetchPlaybooks.rejected, (state, action) => {
        state.list.status = 'failed';
        state.list.error = (action.payload as string) || 'Failed to fetch playbooks';
      })
      .addCase(fetchPlaybookById.pending, (state) => {
        state.current.status = 'loading';
        state.current.error = null;
      })
      .addCase(fetchPlaybookById.fulfilled, (state, action) => {
        state.current.status = 'succeeded';
        const nodes = (action.payload.nodes || []) as Array<any>;
        const selectedNodeId = nodes[0]?.id || null;
        state.current.data = {
          ...action.payload,
          isEdit: true,
          selectedNodeId,
        };
      })
      .addCase(fetchPlaybookById.rejected, (state, action) => {
        state.current.status = 'failed';
        state.current.error = (action.payload as string) || 'Failed to fetch playbook';
      })
      .addCase(createPlaybook.pending, (state) => {
        state.current.status = 'loading';
        state.current.error = null;
      })
      .addCase(createPlaybook.fulfilled, (state, action) => {
        state.current.status = 'succeeded';
        const nodes = (action.payload.nodes || []) as Array<any>;
        const selectedNodeId = nodes[0]?.id || null;
        state.current.data = {
          ...action.payload,
          isEdit: true,
          selectedNodeId,
        };
      })
      .addCase(createPlaybook.rejected, (state, action) => {
        state.current.status = 'failed';
        state.current.error = (action.payload as string) || 'Failed to create playbook';
      })
      .addCase(updatePlaybook.pending, (state) => {
        state.current.status = 'loading';
        state.current.error = null;
      })
      .addCase(updatePlaybook.fulfilled, (state, action) => {
        state.current.status = 'succeeded';
        const nodes = (action.payload.nodes || []) as Array<any>;
        const selectedNodeId = nodes[0]?.id || null;
        state.current.data = {
          ...action.payload,
          isEdit: true,
          selectedNodeId,
        };
      })
      .addCase(updatePlaybook.rejected, (state, action) => {
        state.current.status = 'failed';
        state.current.error = (action.payload as string) || 'Failed to update playbook';
      })
      .addCase(deletePlaybook.pending, (state) => {
        state.list.status = 'loading';
        state.list.error = null;
      })
      .addCase(deletePlaybook.fulfilled, (state, action) => {
        state.list.status = 'succeeded';
        state.list.items = state.list.items.filter((t) => t.id !== action.payload.id);
        if (state.current.data?.id === action.payload.id) {
          state.current = { data: null, status: 'idle', error: null };
        }
      })
      .addCase(deletePlaybook.rejected, (state, action) => {
        state.list.status = 'failed';
        state.list.error = (action.payload as string) || 'Failed to delete playbook';
      })
      .addCase(fetchTemplateForPlaybook.pending, (state) => {
        if (!state.template) {
          state.template = { data: null, status: 'idle', error: null };
        }
        state.template.status = 'loading';
        state.template.error = null;
      })
      .addCase(fetchTemplateForPlaybook.fulfilled, (state, action) => {
        if (!state.template) {
          state.template = { data: null, status: 'idle', error: null };
        }
        state.template.status = 'succeeded';
        state.template.data = action.payload.template;
        state.template.error = null;

        if (!state.current.data) state.current.data = {} as PlaybookDetails;
        state.current.data.nodes = action.payload.nodes;
        state.current.data.edges = action.payload.edges;
        state.current.data.templateId = action.payload.templateId;
        const nodes = (action.payload.nodes || []) as Array<any>;
        const selectedNodeId = nodes[0]?.id || null;
        state.current.data.selectedNodeId = selectedNodeId;
      })
      .addCase(fetchTemplateForPlaybook.rejected, (state, action) => {
        if (!state.template) {
          state.template = { data: null, status: 'idle', error: null };
        }
        state.template.status = 'failed';
        state.template.error =
          (action.payload as string) || 'Failed to fetch template for playbook';
      })
      // Create playbook from JSON
      .addCase(createPlaybookFromJSON.pending, (state) => {
        state.current.status = 'loading';
        state.current.error = null;
      })
      .addCase(createPlaybookFromJSON.fulfilled, (state, action) => {
        state.current.status = 'succeeded';
        const nodes = (action.payload.playbook.nodes || []) as Array<any>;
        const selectedNodeId = nodes[0]?.id || null;
        state.current.data = {
          ...action.payload.playbook,
          isEdit: true,
          selectedNodeId,
        };
      })
      .addCase(createPlaybookFromJSON.rejected, (state, action) => {
        state.current.status = 'failed';
        const payload = action.payload as { message?: string };
        state.current.error = payload?.message || 'Failed to create playbook from JSON';
      });
  },
});

export const {
  clearCurrentPlaybook,
  setCurrentPlaybook,
  updateCurrentPlaybook,
  updatePlaybookField,
  setIsEdit,
  setSelectedNodeId,
  setNodesEdges,
  updateNodeData,
  upsertPlaybookInList,
  clearTemplate,
  setTemplate,
  setFiltersHashForTypes,
  clearFiltersHashForTypes,
} = playbooksSlice.actions;

export default playbooksSlice.reducer;

// Selectors
export const selectPlaybooksList = (state: any) => state.playbooks.list.items as PlaybookSummary[];
export const selectPlaybooksListStatus = (state: any) => state.playbooks.list.status as AsyncStatus;
export const selectCurrentPlaybook = (state: any) =>
  state.playbooks.current.data as PlaybookDetails | null;
export const selectCurrentPlaybookStatus = (state: any) =>
  state.playbooks.current.status as AsyncStatus;
export const selectSelectedNodeId = (state: any) =>
  (state.playbooks.current.data?.selectedNodeId ?? null) as string | null;
export const selectTemplate = (state: any) =>
  state.playbooks.template.data as TemplateDetails | null;
export const selectTemplateStatus = (state: any) => state.playbooks.template.status as AsyncStatus;
export const selectTemplateError = (state: any) => state.playbooks.template.error as string | null;
export const selectFiltersHashForTypes = (state: any) => state.playbooks.filtersHashForTypes;
